import React, { useState, useMemo, useRef, useEffect } from "react";
import "./fleetCardsList.css";
import FleetCard from "../../component/fleet-tracking/FleetCard";

//hooks
import useDebounce from "../../utils/useDebounce";

// Status config — single source of truth for label + dot colour
const STATUS_FILTERS = [
  { key: "all", label: "All", dotClass: "" },
  { key: "online", label: "Online", dotClass: "dot-online" },
  { key: "idle", label: "Idle", dotClass: "dot-idle" },
  { key: "offline", label: "Offline", dotClass: "dot-offline" },
];

// count helpers
const countByStatus = (list) =>
  list.reduce(
    (acc, f) => {
      acc[f.status] = (acc[f.status] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, online: 0, idle: 0, offline: 0 },
  );

const FleetCardsList = ({
  fleetList = [],
  collapsed,
  onFleetSelect,
  selectedFleetId,
  onFleetTrack,
  trackingFleetId,
  isLoading,
  onClose,
}) => {
  if (import.meta.env.DEV) {
    // console.log("debug ujj loading: ", isLoading);
  }
  const scrollWrapperRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState("all");
  const searchRef = useRef(null);

  // Mobile down Swipe-to-Close logic
  useEffect(() => {
    if (window.innerWidth > 768) return;

    const element = scrollWrapperRef.current;
    if (!element) return;
    let touchStartY = 0;
    let touchEndY = 0;
    let scrollTopAtStart = 0;

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
      scrollTopAtStart = element.scrollTop;
    };

    const handleTouchMove = (e) => {
      const currentY = e.touches[0].clientY;
      const delta = currentY - touchStartY;

      // Only trigger close if at the top of scroll and swiping down
      if (scrollTopAtStart === 0 && delta > 50 && delta < 150) {
        element.style.transform = `translateY(${delta}px)`;
        element.style.opacity = `${1 - delta / 200}`;
        e.preventDefault();
      }
      touchEndY = currentY;
    };

    const resetDragStyles = () => {
      element.style.transform = "";
      element.style.opacity = "";
    };

    const handleTouchEnd = () => {
      const delta = touchEndY - touchStartY;
      if (scrollTopAtStart === 0 && delta > 80) {
        resetDragStyles();
        onClose();
      } else {
        element.style.transform = "";
        element.style.opacity = "";
      }
      touchStartY = 0;
      touchEndY = 0;
    };

    element.addEventListener("touchstart", handleTouchStart);
    element.addEventListener("touchmove", handleTouchMove);
    element.addEventListener("touchend", handleTouchEnd);
    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
    };
  }, [collapsed]);

  //keyboard shortcut: "/" focuses search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const counts = useMemo(() => countByStatus(fleetList), [fleetList]);

  const filteredFleetList = useMemo(() => {
    const search = debouncedSearch.toLowerCase(); //compute once

    return fleetList.filter((fleet) => {
      const name = (fleet.vehicleCode || fleet.vehicleId || "").toLowerCase();
      const driver = fleet.employee?.name?.toLowerCase() || "";

      const matchSearch = name.includes(search) || driver.includes(search);
      const matchStatus =
        statusFilter === "all" || fleet.status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [fleetList, debouncedSearch, statusFilter]);

  return (
    <div className={`fleet-list-page ${collapsed ? "fl-collapsed" : ""}`}>
      {/* ── HEADER ─────────────────────────────────────── */}
      <div className="fl-sidebar-header">
        {/* top header */}
        <div className="fl-header-top">
          <div className="fl-brand">
            <i className="bi bi-truck" aria-hidden="true" />
            <span>FLEET</span>
            <span>MASTER</span>
          </div>
        </div>

        <div className="fl-search-field">
          <div className="fl-input-wrapper">
            <i className="bi bi-search fl-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              className="fl-input"
              type="text"
              placeholder="Search driver, registration..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search fleet"
            />
            {!searchTerm && <span className="fl-search-shortcut">/</span>}
            {searchTerm && (
              <button
                className="fl-btn-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        </div>

        {/* Segment control — hidden on mobile */}
        <div
          className="fl-segment-control"
          role="radiogroup"
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map(({ key, label, dotClass }) => (
            <button
              key={key}
              className={`fl-segment-item ${statusFilter === key ? "active" : ""}`}
              onClick={() => setStatusFilter(key)}
              role="radio"
              aria-checked={statusFilter === key}
            >
              <span className="segment-label">{label}</span>
              <div className="segment-item-count">
                {dotClass && <span className={`segment-dot ${dotClass}`} />}
                <span className="segment-count">{counts[key]}</span>
              </div>
            </button>
          ))}
        </div>
        {/* {s === "all" ? "All" : s === "online" ? " Online" : " Offline"} */}

        {/* <div
          className="fl-live-badge"
          title={`${counts.online} online, ${counts.idle} idle`}
        >
          <span className="fl-live-dot" />
          <span>{counts.online + counts.idle}</span>
        </div> */}

        {/* Results summary line */}
        <div className="fl-summary">
          {isLoading ? (
            <span className="fl-loading-line">
              <span className="fl-spinner" /> Syncing fleet...
            </span>
          ) : (
            <span>
              Showing <strong>{filteredFleetList.length}</strong> of{" "}
              {fleetList.length} vehicles
            </span>
          )}
        </div>
      </div>

      <div ref={scrollWrapperRef} className="fl-scroll-wrapper" role="list">
        {filteredFleetList.length === 0 ? (
          <div className="empty-state-3d">
            <i className="bi bi-binoculars fl-empty-icon" />
            <p className="fl-empty-title">No vehicles found</p>
            {searchTerm && (
              <button
                className="fl-empty-reset"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          filteredFleetList.map((fleet, index) => (
            <FleetCard
              key={fleet.vehicleId}
              index={index}
              fleet={fleet}
              onSelect={onFleetSelect}
              isSelected={selectedFleetId === fleet.vehicleId}
              onTrack={onFleetTrack}
              isTracking={trackingFleetId === fleet.vehicleId}
            />
          ))
        )}
      </div>
    </div>
  );
};

// parent re-renders every 5s
// without memo → entire list re-renders
// with memo → only updates when props change
export default React.memo(FleetCardsList);
